import windowSize from "../services/WindowSize.js";
import Swiper from "swiper";
import "swiper/css";
import { Autoplay, FreeMode, Navigation } from "swiper/modules";

/**
 * Swiper module
 *
 * @package AGDS
 * @category Modules
 * @module Swiper
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class SwiperSlider {
    /**
     * Constructor
     *
     * @param {Object} options
     */
    constructor(options = {}) {
        this.swiperEls = options.swiperEls;

        this.defaultOptions = {
            speed: 800,
            slidesPerView: 1,
            spaceBetween: 16,
        };
    }

    /**
     * Initialize
     */
    init() {
        if (typeof Swiper === "undefined") {
            console.error("Swiper is not defined");
            return;
        }

        // Sliders
        this.clientsSliderInit();
        this.reviewsSliderInit();
        this.gallerySliderInit();
        this.catalogSliderInit();

        // Mobile
        this.unsubWinSizeMob = windowSize.onBelow(
            1200,
            () => {
                this.cardsSliderInit();
            },
            { immediate: true },
        );
    }

    /**
     * Створює слайдер і зливає (merges) налаштування
     * @param {HTMLElement|string} elementOrSelector - DOM-елемент або CSS селектор
     * @param {Object} customOptions - Унікальні параметри для цього слайдера
     * @returns {Array} Масив інстансів Swiper
     */
    createSlider(elementOrSelector, customOptions = {}) {
        const elements =
            typeof elementOrSelector === "string" ? document.querySelectorAll(elementOrSelector) : [elementOrSelector];

        if (!elements || elements.length === 0) return;

        // Зливаємо дефолтні опції з кастомними.
        const finalOptions = {
            ...this.defaultOptions,
            ...customOptions,
            breakpoints: {
                ...this.defaultOptions.breakpoints,
                ...(customOptions.breakpoints || {}),
            },
        };

        const instances = [];

        elements.forEach((el) => {
            if (el.classList.contains("swiper-initialized")) {
                return;
            }

            const swiper = new Swiper(el, finalOptions);

            // Класи
            swiper.on("init", (s) => {
                if (s.params.centeredSlides) {
                    s.el.classList.add("swiper-centered-slides");
                }
                if (s.params.loop) {
                    s.el.classList.add("swiper-is-loop");
                }
            });

            // Якщо Swiper вже ініціалізувався (іноді буває миттєво),
            // викликаємо init вручну один раз для класів
            if (swiper.initialized) {
                swiper.emit("init");
            }

            instances.push(swiper);
        });

        return instances;
    }

    /**
     * Cards Slider
     */
    cardsSliderInit() {
        this.createSlider(".cards-slider", {
            breakpoints: {
                992: { slidesPerView: 3, spaceBetween: 16 },
                768: { slidesPerView: 2.5, spaceBetween: 14 },
                320: { slidesPerView: 1.15, spaceBetween: 12 },
            },
        });
    }

    /**
     * Gallery Slider
     */
    gallerySliderInit() {
        this.createSlider(".gallery-slider", {
            modules: [Navigation, Autoplay],
            autoplay: {
                delay: 3000,
                disableOnInteraction: true,
                pauseOnMouseEnter: true,
            },
            loop: true,
            navigation: {
                nextEl: ".gallery-section .nav-button--next",
                prevEl: ".gallery-section .nav-button--prev",
            },
            breakpoints: {
                992: { slidesPerView: 4, spaceBetween: 16 },
                768: { slidesPerView: 2.5, spaceBetween: 14 },
                320: { slidesPerView: 1.15, spaceBetween: 12 },
            },
        });
    }

    /**
     * Catalog Slider
     */
    catalogSliderInit() {
        document.querySelectorAll(".catalog-slider").forEach((slider) => {
            const section = slider.closest(".catalog-section");

            if (!section) {
                return;
            }

            this.createSlider(slider, {
                modules: [Navigation],
                navigation: {
                    nextEl: section.querySelector(".nav-button--next"),
                    prevEl: section.querySelector(".nav-button--prev"),
                },
                breakpoints: {
                    1200: { slidesPerView: 3, spaceBetween: 20 },
                    992: { slidesPerView: 2.5, spaceBetween: 16 },
                    768: { slidesPerView: 2.5, spaceBetween: 10 },
                    320: { slidesPerView: 2.5, spaceBetween: 6 },
                },
            });
        });
    }

    /**
     * Clients Slider (Marquee 2 rows)
     */
    clientsSliderInit() {
        const sliders = document.querySelectorAll(".clients-slider");
        if (!sliders.length) return;

        sliders.forEach((slider) => {
            const isReverse = slider.dataset.reverse === "true";

            this.createSlider(slider, {
                modules: [Autoplay, FreeMode],
                speed: 2000,
                loop: true,
                allowTouchMove: false,
                autoplay: {
                    delay: 0,
                    disableOnInteraction: false,
                    reverseDirection: isReverse,
                },
                breakpoints: {
                    1200: { slidesPerView: 6, spaceBetween: 16 },
                    768: { slidesPerView: 3.5, spaceBetween: 16 },
                    320: { slidesPerView: 2.5, spaceBetween: 16 },
                },
            });
        });
    }

    /**
     * Reviews Slider
     */
    reviewsSliderInit() {
        this.createSlider(".reviews-slider", {
            modules: [Navigation],
            navigation: {
                nextEl: ".reviews-section .nav-button--next",
                prevEl: ".reviews-section .nav-button--prev",
            },
            breakpoints: {
                1200: { slidesPerView: 3, spaceBetween: 14 },
                768: { slidesPerView: 2.5, spaceBetween: 16 },
                320: { slidesPerView: 1.15, spaceBetween: 16 },
            },
        });
    }
}
